import { useQuery } from '@tanstack/react-query'
import { Building2, LayoutDashboard, MessageCircle, Phone, TrendingUp, Users } from 'lucide-react'
import { fetchAdminOverview } from '../../api'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/skeleton'
import { Sparkline } from '../../components/Sparkline'
import { PageHeader } from '../../components/PageHeader'
import { useTrend } from '../../hooks/useTrend'

function StatCard({
  label,
  value,
  icon: Icon,
  isLoading,
}: {
  label: string
  value: number | undefined
  icon: typeof Building2
  isLoading: boolean
}) {
  const trend = useTrend(value)

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="flex items-end justify-between gap-3">
        {isLoading || value === undefined ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <p className="text-2xl font-semibold tracking-tight tabular-nums">{value.toLocaleString('es-CR')}</p>
        )}
        {trend.length >= 2 && <Sparkline data={trend} className="h-7 w-14 shrink-0 text-primary" />}
      </CardContent>
    </Card>
  )
}

export function OverviewPage() {
  const query = useQuery({ queryKey: ['admin', 'overview'], queryFn: fetchAdminOverview })

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={LayoutDashboard}
        title="Resumen de la plataforma"
        description="Visibilidad de solo lectura sobre todos los tenants."
      />

      {query.isError && (
        <p className="text-sm text-destructive">No se pudo cargar el resumen de la plataforma.</p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Empresas" value={query.data?.total_empresas} icon={Building2} isLoading={query.isLoading} />
        <StatCard label="Sociedades" value={query.data?.total_sociedades} icon={MessageCircle} isLoading={query.isLoading} />
        <StatCard label="Usuarios" value={query.data?.total_usuarios} icon={Users} isLoading={query.isLoading} />
        <StatCard
          label="Números de WhatsApp activos"
          value={query.data?.total_channel_accounts_activos}
          icon={Phone}
          isLoading={query.isLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard
          label="Empresas nuevas (7 días)"
          value={query.data?.empresas_nuevas_7d}
          icon={TrendingUp}
          isLoading={query.isLoading}
        />
        <StatCard
          label="Empresas nuevas (30 días)"
          value={query.data?.empresas_nuevas_30d}
          icon={TrendingUp}
          isLoading={query.isLoading}
        />
      </div>
    </div>
  )
}
